import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { kaireraAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Building2, Loader2, TrendingUp, Shield, Lightbulb, Zap,
  AlertTriangle, Target, ArrowUpRight, Calendar, Award,
  Users, ChevronRight, BarChart3, Heart, Info, Star
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell, AreaChart, Area, LineChart, Line
} from 'recharts';

const SCORE_COLORS = { high: '#16A34A', mid: '#F5841F', low: '#DC2626' };
const scoreColor = (v) => v >= 70 ? SCORE_COLORS.high : v >= 40 ? SCORE_COLORS.mid : SCORE_COLORS.low;

function ScoreGauge({ value, label, size = 80, delay = 0 }) {
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const color = scoreColor(value);
  return (
    <div className="text-center">
      <div className="relative mx-auto" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={r} fill="none" stroke="var(--t-border)" strokeWidth="7" />
          <motion.circle cx="50" cy="50" r={r} fill="none"
            stroke={color} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ * (1 - value / 100) }}
            transition={{ duration: 1.2, delay }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-black" style={{ color }}>{value}</span>
        </div>
      </div>
      <p className="text-[9px] font-bold mt-1 uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>{label}</p>
    </div>
  );
}

export default function CompanyIntelPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCompany, setHasCompany] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await kaireraAPI.getCompanyIntel();
        setHasCompany(res.data.has_company);
        setData(res.data.company_intel);
      } catch { setHasCompany(false); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#3B82F6' }} />
        <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>Analyzing company intelligence...</p>
        <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>This may take a moment on first load</p>
      </div>
    </div>
  );

  if (!hasCompany || !data) return (
    <div className="max-w-lg mx-auto mt-20 text-center" data-testid="company-no-data">
      <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
        style={{ background: 'var(--t-bg-section)', border: '2px dashed var(--t-border)' }}>
        <Building2 className="w-8 h-8" style={{ color: 'var(--t-text-muted)' }} />
      </div>
      <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>No Company Data Yet</h2>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-muted)' }}>
        Complete the Career Discovery and provide your current company information to unlock Company Intelligence.
        If this is your first job, this page will populate once you provide employer details.
      </p>
    </div>
  );

  const outlook = data.twelve_month_outlook || {};
  const quarters = outlook.quarters || [];

  // Build opportunity score line chart data
  const quarterData = quarters.map(q => ({
    name: q.quarter,
    score: q.opportunity_score,
  }));

  // Health metrics for radar
  const healthData = [
    { metric: 'Health', value: data.health_score || 0 },
    { metric: 'Culture', value: data.culture_score || 0 },
    { metric: 'Innovation', value: data.innovation_score || 0 },
    { metric: 'Stability', value: data.stability_score || 0 },
  ];

  const TREND_COLORS = { positive: '#16A34A', negative: '#DC2626', neutral: '#F5841F' };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="company-intel-page">
      {/* Header */}
      <div>
        <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-black tracking-tight" style={{ color: 'var(--t-text-heading)' }}>
          <Building2 className="w-6 h-6 inline mr-2 -mt-1" style={{ color: '#3B82F6' }} />
          {data.company_name}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-sm mt-1" style={{ color: 'var(--t-text-muted)' }}>
          {data.industry} {data.company_size ? `  |  ${data.company_size}` : ''} {data.growth_stage ? `  |  ${data.growth_stage}` : ''}
        </motion.p>
      </div>

      {/* Overview Card */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="nx-card p-5" data-testid="company-overview">
        <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{data.overview}</p>
      </motion.div>

      {/* Health Scores */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="nx-card p-5" data-testid="company-health-scores">
        <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: '#3B82F6' }}>
          Company Health Scores
        </p>
        <div className="grid grid-cols-4 gap-4">
          {healthData.map((h, i) => (
            <ScoreGauge key={h.metric} value={h.value} label={h.metric} delay={0.3 + i * 0.15} />
          ))}
        </div>
      </motion.div>

      {/* Strengths + Risks Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Strengths */}
        <motion.div initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 }}
          className="nx-card p-5" data-testid="company-strengths">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#16A34A' }}>
            Company Strengths
          </p>
          <div className="space-y-2">
            {(data.strengths || []).map((s, i) => (
              <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg"
                style={{ background: '#16A34A06', border: '1px solid #16A34A15' }}>
                <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                <span className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{s}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Risks */}
        <motion.div initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
          className="nx-card p-5" data-testid="company-risks">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#DC2626' }}>
            Risks to Watch
          </p>
          <div className="space-y-2">
            {(data.risks || []).map((r, i) => (
              <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg"
                style={{ background: '#DC262606', border: '1px solid #DC262615' }}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                <span className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{r}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Industry Trends */}
      {(data.industry_trends || []).length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
          className="nx-card p-5" data-testid="company-industry-trends">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#8B5CF6' }}>
            Industry Trends Impacting {data.company_name}
          </p>
          <div className="space-y-2.5">
            {data.industry_trends.map((t, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2.5 rounded-lg"
                style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ background: TREND_COLORS[t.impact] || TREND_COLORS.neutral }} />
                <div>
                  <p className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>{t.trend}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{t.detail}</p>
                </div>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full ml-auto flex-shrink-0 capitalize"
                  style={{ background: `${TREND_COLORS[t.impact] || TREND_COLORS.neutral}12`, color: TREND_COLORS[t.impact] }}>
                  {t.impact}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* 12-Month Outlook */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        data-testid="company-12month-outlook">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5" style={{ color: '#F5841F' }} />
          <h2 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Your Next 12 Months</h2>
          {outlook.overall_trajectory && (
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full capitalize ml-2"
              style={{
                background: outlook.overall_trajectory === 'growing' ? '#16A34A12' : '#F5841F12',
                color: outlook.overall_trajectory === 'growing' ? '#16A34A' : '#F5841F',
              }}>
              {outlook.overall_trajectory}
            </span>
          )}
        </div>

        {/* Key Stats Row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="nx-card p-4 text-center">
            <p className="text-2xl font-black" style={{ color: '#8B5CF6' }}>{outlook.promotion_likelihood || 'N/A'}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Promotion Likelihood</p>
          </div>
          <div className="nx-card p-4 text-center">
            <p className="text-2xl font-black" style={{ color: '#16A34A' }}>{outlook.salary_growth_pct || 0}%</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Salary Growth</p>
          </div>
          <div className="nx-card p-4 text-center">
            <p className="text-2xl font-black" style={{ color: '#3B82F6' }}>{quarters.length}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>Quarters Mapped</p>
          </div>
        </div>

        {/* Opportunity Score Trend */}
        {quarterData.length > 0 && (
          <div className="nx-card p-5 mb-4" data-testid="company-opportunity-chart">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#F5841F' }}>
              Opportunity Score Trend
            </p>
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={quarterData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <defs>
                    <linearGradient id="oppGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F5841F" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F5841F" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                  <Tooltip contentStyle={{
                    background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                    borderRadius: 10, fontSize: 11,
                  }} formatter={v => [`${v}/100`, 'Opportunity']} />
                  <Area type="monotone" dataKey="score" stroke="#F5841F" fill="url(#oppGrad)"
                    strokeWidth={2.5} dot={{ fill: '#F5841F', r: 5, stroke: '#fff', strokeWidth: 2 }}
                    animationDuration={1500} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Quarter Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {quarters.map((q, i) => {
            const qColor = ['#3B82F6', '#8B5CF6', '#F5841F', '#16A34A'][i % 4];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className="nx-card p-4 overflow-hidden relative"
                style={{ borderLeft: `4px solid ${qColor}` }}
                data-testid={`company-quarter-${q.quarter}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-black" style={{ color: qColor }}>{q.quarter}</span>
                    <span className="text-xs font-bold" style={{ color: 'var(--t-text-heading)' }}>{q.title}</span>
                  </div>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: `${qColor}12`, color: qColor }}>
                    {q.opportunity_score}/100
                  </span>
                </div>
                <ul className="space-y-1">
                  {(q.events || []).map((e, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: qColor }} />
                      <span className="text-[11px]" style={{ color: 'var(--t-text-secondary)' }}>{e}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Key Milestones */}
        {(outlook.key_milestones || []).length > 0 && (
          <div className="nx-card p-5 mb-4" data-testid="company-milestones">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#16A34A' }}>
              Key Milestones to Target
            </p>
            <div className="space-y-2">
              {outlook.key_milestones.map((m, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
                  style={{ background: '#16A34A06', border: '1px solid #16A34A15' }}>
                  <Target className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                  <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{m}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risks to Watch */}
        {(outlook.risks_to_watch || []).length > 0 && (
          <div className="nx-card p-5 mb-4" data-testid="company-outlook-risks">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#DC2626' }}>
              Risks to Watch
            </p>
            <div className="space-y-2">
              {outlook.risks_to_watch.map((r, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
                  style={{ background: '#DC262606', border: '1px solid #DC262615' }}>
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                  <span className="text-xs" style={{ color: 'var(--t-text-secondary)' }}>{r}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Career Advice */}
      {data.career_advice && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
          className="nx-card p-5" style={{ borderLeft: '4px solid #3B82F6' }}
          data-testid="company-career-advice">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4" style={{ color: '#3B82F6' }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#3B82F6' }}>
              Personalized Advice
            </span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--t-text-heading)' }}>
            {data.career_advice}
          </p>
        </motion.div>
      )}

      {/* Sources */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}
        className="nx-card p-4" data-testid="company-sources">
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-3.5 h-3.5" style={{ color: '#0D9488' }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#0D9488' }}>
            Data Sources
          </span>
        </div>
        <p className="text-[10px] mb-2" style={{ color: 'var(--t-text-muted)' }}>
          {data.data_source || 'Intelligence compiled from web research and AI analysis.'}
        </p>
        {(data.sources || []).filter(s => s.url).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {data.sources.filter(s => s.url).map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                className="text-[10px] font-semibold px-2 py-1 rounded-md inline-flex items-center gap-1 transition-colors"
                style={{ background: '#0D948808', color: '#0D9488', border: '1px solid #0D948820' }}>
                <ArrowUpRight className="w-3 h-3" />
                {s.title}
              </a>
            ))}
          </div>
        )}
        {(data.sources || []).filter(s => s.url).length === 0 && (
          <p className="text-[10px] italic" style={{ color: 'var(--t-text-muted)' }}>
            Analysis based on AI knowledge. For deeper insights, company data APIs can be integrated.
          </p>
        )}
      </motion.div>
    </div>
  );
}
